/**
* @license Apache-2.0
*
* Copyright (c) 2023 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isFunction from '@stdlib/assert-is-function';
import { isPrimitive as isString } from '@stdlib/assert-is-string';
import isPlainObject from '@stdlib/assert-is-plain-object';
import hasOwnProp from '@stdlib/assert-has-own-property';
import { factory as contains } from '@stdlib/array-base-assert-contains';
import forEachCodeUnit from '@stdlib/string-base-for-each';
import forEachCodePoint from '@stdlib/string-base-for-each-code-point';
import forEachGraphemeCluster from '@stdlib/string-base-for-each-grapheme-cluster';
import format from '@stdlib/error-tools-fmtprodmsg';


// VARIABLES //

var MODES = [ 'grapheme', 'code_point', 'code_unit' ];
var FCNS = {
	'grapheme': forEachGraphemeCluster,
	'code_point': forEachCodePoint,
	'code_unit': forEachCodeUnit
};
var isMode = contains( MODES );


// MAIN //

/**
* Invokes a function for each character in a string.
*
* @param {string} str - input string
* @param {Options} [options] - options
* @param {string} [options.mode="grapheme"] - type of "character" over which to iterate (must be either `grapheme`, `code_point`, or `code_unit`)
* @param {Function} clbk - function to invoke
* @param {*} [thisArg] - execution context
* @throws {TypeError} first argument must be a string
* @throws {TypeError} callback argument must be a function
* @throws {TypeError} options argument must be an object
* @throws {TypeError} must provide valid options
* @returns {string} input string
*
* @example
* function log( value, index ) {
*     console.log( '%d: %s', index, value );
* }
*
* forEach( 'Hello', log );
*/
function forEach( str, options, clbk ) {
	var thisArg;
	var nargs;
	var opts;
	var cb;
	if ( !isString( str ) ) {
		throw new TypeError( format( '1gr3F', str ) );
	}
	opts = {
		'mode': 'grapheme'
	};
	nargs = arguments.length;
	if ( nargs === 2 ) {
		cb = options;
		options = null;
	} else if ( nargs === 3 ) {
		if ( isPlainObject( options ) ) {
			cb = clbk;
		} else {
			cb = options;
			options = null;
			thisArg = clbk;
		}
	} else { // nargs === 4
		if ( !isPlainObject( options ) ) {
			throw new TypeError( format( '1gr2V', options ) );
		}
		cb = clbk;
		thisArg = arguments[ 3 ];
	}
	if ( !isFunction( cb ) ) {
		throw new TypeError( format( '1gr2b', cb ) );
	}
	if ( options ) {
		if ( hasOwnProp( options, 'mode' ) ) {
			opts.mode = options.mode;
			if ( !isMode( opts.mode ) ) {
				throw new TypeError( format( '1grE1', 'mode', MODES.join( '", "' ), opts.mode ) );
			}
		}
	}
	FCNS[ opts.mode ]( str, cb, thisArg );
	return str;
}


// EXPORTS //

export default forEach;
